/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { CleanIconButton, TextLink, Typography } from '@neo4j-ndl/react';
import {
  Prompt,
  Response,
  Suggestion,
  Thinking,
  UserBubble,
} from '@neo4j-ndl/react/ai';
import {
  ArrowPathIconOutline,
  Cog6ToothIconOutline,
  HandThumbDownIconOutline,
  PlusIconOutline,
  Square2StackIconOutline,
  XMarkIconOutline,
} from '@neo4j-ndl/react/icons';
import { useEffect, useRef, useState } from 'react';

const FAKE_RESPONSES = [
  `Here is a simple response with some **bold text** and *italics*.`,
  `Here is a list of items:
- Item 1
- Item 2
- Item 3`,
  `Here is a code block example:

\`\`\`typescript
const greeting = "Hello World";
console.log(greeting);
\`\`\`
`,
  `# Heading 1
## Heading 2
### Heading 3

Some text under headings.`,
  `You can also use tables:

| Header 1 | Header 2 |
|Data 1|Data 2|
|Data 3|Data 4|
`,
];

export const Component = () => {
  const [messages, setMessages] = useState<
    {
      role: 'user' | 'assistant';
      content: string;
      thinkingTime?: number;
      isDone?: boolean;
    }[]
  >([]);
  const [prompt, setPrompt] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [responseIndex, setResponseIndex] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking]);

  const handleCancel = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setIsThinking(false);
    setIsStreaming(false);

    // Mark the last message as done if stopped
    setMessages((prev) => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage?.role === 'assistant') {
        lastMessage.isDone = true;
      }
      return newMessages;
    });
  };

  const handleSend = (overridePrompt?: string) => {
    const textToSend = overridePrompt || prompt;

    if (!textToSend.trim()) {
      return;
    }

    setMessages((prev) => [...prev, { content: textToSend, role: 'user' }]);
    setPrompt('');
    setIsThinking(true);
    const startTime = Date.now();

    // Simulate network delay (thinking time)
    timeoutRef.current = setTimeout(() => {
      const endTime = Date.now();
      const thinkingTime = endTime - startTime;
      setIsThinking(false);
      setIsStreaming(true);

      const responseText = FAKE_RESPONSES[responseIndex];
      setResponseIndex((prev) => (prev + 1) % FAKE_RESPONSES.length);

      let currentText = '';
      setMessages((prev) => [
        ...prev,
        { content: '', isDone: false, role: 'assistant', thinkingTime },
      ]);

      // Simulate streaming
      intervalRef.current = setInterval(() => {
        if (currentText.length < responseText.length) {
          // Add a few characters at a time to simulate chunks
          // Ensure we don't split newlines incorrectly if that's an issue,
          // but simple slicing should be fine as long as the source has \n.
          const chunk = responseText.slice(
            currentText.length,
            currentText.length + 2,
          );
          currentText += chunk;

          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.role === 'assistant') {
              lastMessage.content = currentText;
            }
            return newMessages;
          });
        } else {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          setIsStreaming(false);
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.role === 'assistant') {
              lastMessage.isDone = true;
            }
            return newMessages;
          });
        }
      }, 50);
    }, 2000);
  };

  return (
    <section className="n-h-screen">
      <div className="n-w-[440px] n-h-full n-flex n-flex-col n-bg-neutral-bg-weak">
        <div className="n-flex n-flex-row n-border-b n-border-neutral-border-weak n-p-3">
          <div className="n-ml-auto">
            <CleanIconButton description="settings" tooltipProps={{}}>
              <Cog6ToothIconOutline />
            </CleanIconButton>
            <CleanIconButton description="close">
              <XMarkIconOutline />
            </CleanIconButton>
          </div>
        </div>
        <div className="n-p-4 n-flex n-flex-col n-grow n-overflow-y-auto">
          {messages.length === 0 ? (
            <div className="n-flex n-flex-col ">
              <div className="n-flex n-flex-col n-gap-12">
                <Typography variant="display">
                  Hi [User], how can I help you today?
                </Typography>
                <div className="n-flex n-flex-col n-gap-4">
                  <Typography variant="body-medium">Suggestions</Typography>
                  <Suggestion
                    isPrimary
                    onClick={() => {
                      handleSend('I want to import data');
                    }}
                  >
                    I want to import data
                  </Suggestion>
                  <Suggestion
                    onClick={() => {
                      handleSend('Create an AI agent');
                    }}
                  >
                    Create an AI agent
                  </Suggestion>
                  <Suggestion
                    onClick={() => {
                      handleSend('Invite project members');
                    }}
                  >
                    Invite project members
                  </Suggestion>
                  <Suggestion
                    onClick={() => {
                      handleSend('Generate a report');
                    }}
                  >
                    Generate a report
                  </Suggestion>
                </div>
                <Typography variant="body-medium">
                  You can also drag and drop files here, or{' '}
                  <TextLink as="button" type="internal-underline">
                    browse
                  </TextLink>
                  . Supports CVG, MOV, PDF
                </Typography>
              </div>
            </div>
          ) : (
            <div className="n-flex n-flex-col n-gap-4 n-pb-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`n-flex ${
                    msg.role === 'user' ? 'n-justify-end' : 'n-justify-start'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <div className="n-max-w-[85%]">
                      <UserBubble
                        avatarProps={{
                          name: 'NM',
                          type: 'letters',
                        }}
                      >
                        {msg.content}
                      </UserBubble>
                    </div>
                  ) : (
                    <div className="n-w-full n-flex n-flex-col n-gap-2">
                      {msg.thinkingTime !== undefined && (
                        <Thinking
                          isThinking={false}
                          thinkingMs={msg.thinkingTime}
                        />
                      )}
                      <div className="n-flex n-flex-col n-gap-2">
                        <Response>{msg.content}</Response>

                        {msg.isDone === true && (
                          <div className="n-flex n-flex-row n-gap-1.5">
                            <CleanIconButton size="small" description="Dislike">
                              <HandThumbDownIconOutline />
                            </CleanIconButton>
                            <CleanIconButton size="small" description="Re-run">
                              <ArrowPathIconOutline />
                            </CleanIconButton>
                            <CleanIconButton size="small" description="Copy">
                              <Square2StackIconOutline />
                            </CleanIconButton>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isThinking && <Thinking isThinking={true} />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
        <div className="n-px-4 n-pt-4 n-pb-1 n-mt-auto">
          <Prompt
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            onSubmitPrompt={() => handleSend()}
            onCancelPrompt={handleCancel}
            isRunningPrompt={isThinking || isStreaming}
            isSubmitDisabled={
              prompt.length === 0 && !(isThinking || isStreaming)
            }
            bottomContent={
              <CleanIconButton description="Add files" size="small">
                <PlusIconOutline />
              </CleanIconButton>
            }
          />
        </div>
      </div>
    </section>
  );
};

export default Component;
